import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/use-auth';

/**
 * RequireAuth guard - wraps authenticated routes.
 *
 * Defense-in-depth: AppRouter's AuthGate already discriminates on auth.status
 * at the top level, so the AppShell tree only renders when status === 'ready'.
 * RequireAuth's status check is therefore redundant in the current router
 * layout, but kept so that any direct route registration outside AuthGate
 * (e.g. future deep-linked dev pages) still gets the check.
 *
 * Behavior:
 *   - loading: render nothing (avoid flash; AuthGate handles this above)
 *   - unauthenticated: redirect to /login, preserving the original path so
 *     the user can be sent back after sign-in
 *   - unprovisioned: render the children anyway; AppRouter routes
 *     unprovisioned users to ProfileNotProvisioned at the AuthGate level,
 *     so reaching RequireAuth in this state should be impossible. Returning
 *     null here is the safe fail-closed default.
 *   - ready: render the children
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const location = useLocation();

  if (auth.status === 'loading') {
    return null;
  }

  if (auth.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (auth.status === 'unprovisioned') {
    // Should be unreachable given AuthGate's routing, but fail closed.
    return null;
  }

  return <>{children}</>;
}
